import type { ModuleResult } from '../../gen/ejot/roofing/v1/types';
import { formatQuantityWithUnit } from '../../shared/format';

export interface WindSummaryFact {
  label: string;
  value: string;
}

/** Builds display facts exclusively from backend calculation quantities. */
export function buildWindSummaryFacts(module: ModuleResult | undefined): WindSummaryFact[] {
  if (!module?.wind) return [];
  const facts: WindSummaryFact[] = [];
  const push = (label: string, quantity: Parameters<typeof formatQuantityWithUnit>[0]) => {
    if (quantity) facts.push({ label, value: formatQuantityWithUnit(quantity, 'Unavailable') });
  };

  push('Fundamental basic wind velocity', module.wind_input?.base_wind_velocity);
  push('Basic wind velocity', module.wind.basic_wind_velocity);
  push('Basic velocity pressure', module.wind.basic_velocity_pressure);
  push('Exposure factor', module.wind.exposure_factor);
  push('Peak velocity pressure', module.wind.peak_velocity_pressure);
  push('Load safety factor', module.wind.load_safety_factor);
  for (const [index, coefficient] of (module.wind.internal_pressure_coefficients ?? []).entries()) {
    push(`Internal pressure case ${index + 1}`, coefficient);
  }
  return facts;
}

export function WindCalculationSummary({ module }: { module: ModuleResult | undefined }) {
  const facts = buildWindSummaryFacts(module);
  if (facts.length === 0) {
    return (
      <section className="result-section" aria-label="Wind calculation summary">
        <h3 className="result-section-title">Wind calculation</h3>
        <p className="empty-hint">No backend wind calculation is available yet.</p>
      </section>
    );
  }

  return (
    <section className="result-section" aria-label="Wind calculation summary">
      <h3 className="result-section-title">Wind calculation</h3>
      <dl className="definition-list">
        {module?.wind_input?.national_dataset_id && (
          <><dt>National dataset</dt><dd>{module.wind_input.national_dataset_id}</dd></>
        )}
        {module?.wind_input?.wind_zone_id && (
          <><dt>Wind zone</dt><dd>{module.wind_input.wind_zone_id}</dd></>
        )}
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
